/**
 * Language Sync Hook
 *
 * Syncs UI language between i18next, localStorage, and backend.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getPreferredLanguage, updatePreferredLanguage } from '../services/api';
import { saveLanguage, SUPPORTED_LANGUAGES } from '../i18n';

export function useLanguageSync(): {
  changeLanguage: (lang: string) => Promise<void>;
  currentLanguage: string;
} {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const syncedRef = useRef(false);

  // On login, sync language from backend
  useEffect(() => {
    if (!isAuthenticated || syncedRef.current) return;
    syncedRef.current = true;

    getPreferredLanguage()
      .then((lang) => {
        if (SUPPORTED_LANGUAGES.includes(lang) && lang !== i18n.language) {
          i18n.changeLanguage(lang);
          saveLanguage(lang);
        }
      })
      .catch(() => {
        // Ignore - use local preference
      });
  }, [isAuthenticated, i18n]);

  // Reset sync flag on logout
  useEffect(() => {
    if (!isAuthenticated) {
      syncedRef.current = false;
    }
  }, [isAuthenticated]);

  const changeLanguage = useCallback(async (lang: string) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return;
    await i18n.changeLanguage(lang);
    saveLanguage(lang);
    if (isAuthenticated) {
      try {
        await updatePreferredLanguage(lang);
      } catch {
        // Language changed locally even if backend fails
      }
    }
  }, [i18n, isAuthenticated]);

  return {
    changeLanguage,
    currentLanguage: i18n.language,
  };
}
